/* src/components/Admin/Organizations/EmployeeSelector.tsx */
import React from 'react';
import { OrganizationItem } from '../../../api/adminOrganizationsService';
import { useTranslation } from 'react-i18next';
import FilterButton from '../Investigations/FilterButton';

interface Props {
  org: OrganizationItem | null;
  selected: string[];
  onChange: (emails: string[]) => void;
}

const EmployeeSelector: React.FC<Props> = ({ org, selected, onChange }) => {
  const { t } = useTranslation();

  if (!org) {
    return (
      <div className="h-36 flex items-center justify-center border rounded-xl bg-white shadow">
        <p className="text-gray-400">{t('admin.select_company_hint')}</p>
      </div>
    );
  }

  /* ordenar por score ascendente */
  const employees = [...org.employees].sort((a, b) => a.score - b.score);

  /* icono/badge por tipo */
  const badge = (kind: string) => {
    switch (kind) {
      case 'priority':  return '⭐';
      case 'generic':   return '📥';
      case 'predicted': return '🤖';
      default:          return '';
    }
  };

  /* toggle manual */
  const toggle = (email: string) =>
    selected.includes(email)
      ? onChange(selected.filter((e) => e !== email))
      : onChange([...selected, email]);

  /* -------- nuevo: auto-select top 2 -------- */
  const selectTopTwo = () => {
    const top = employees.slice(0, 2).map((e) => e.email);
    onChange(top);
  };

  return (
    <div className="border rounded-xl bg-white shadow p-4 overflow-y-auto max-h-60">
      {/* Cabecera + botón */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium">
          {t('admin.recipients')} ({employees.length}/{org.total_candidates})
        </h4>

        {/* Botón auto-selección */}
        <FilterButton onClick={selectTopTwo}>
          {t('admin.auto_select_two')} {/* añade esta clave al i18n */}
        </FilterButton>
      </div>

      {/* Lista de candidatos */}
      {employees.map((emp) => {
        const fullName = `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim();
        return (
          <label key={emp.email} className="flex items-start gap-2 text-sm mb-1">
            <input
              type="checkbox"
              checked={selected.includes(emp.email)}
              onChange={() => toggle(emp.email)}
              className="mt-1 accent-blue-600"
            />
            <div>
              <span className="font-medium">
                {badge(emp.kind)}{' '}
                {fullName || emp.title || 'Employee'}
              </span>
              {emp.title && fullName && (
                <span className="text-gray-500"> — {emp.title}</span>
              )}
              {emp.headline && (
                <div className="text-xs italic text-gray-500">
                  {emp.headline}
                </div>
              )}
              <div className="text-xs text-blue-700">{emp.email}</div>
            </div>
          </label>
        );
      })}

      {employees.length === 0 && (
        <p className="text-xs text-gray-400">{t('admin.no_candidates')}</p>
      )}
    </div>
  );
};

export default EmployeeSelector;
